/**
 * Store rating prompt — when to ask, and the one call that asks.
 *
 * The ONLY rating UI in this app is the operating system's own sheet:
 * `SKStoreReviewController` on iOS, Play In-App Review on Android. Stars and
 * comments are collected, stored and moderated by Apple / Google, and show up
 * in App Store Connect / Play Console. We keep no ratings of our own — no star
 * widget, no table, no API.
 *
 * Neither store tells the app what happened. There is no "shown", no "rated",
 * no "dismissed", and the OS is free to show nothing at all (Apple allows the
 * sheet at most 3 times per app per 365 days; Google applies a quota it does
 * not publish). So `requestStoreReview()` resolves once the request has been
 * handed to the OS, whatever followed, and our own history below is the only
 * record there is — which is why every ask is written down BEFORE it is made.
 *
 * THE POLICY (`evaluateEligibility`, pure) — an ask is allowed only when ALL hold:
 *   - native app (never the browser, never an installed PWA);
 *   - the role is one that can use the feedback route (not KIOSK, DRIVER or
 *     CONTROL_CENTER);
 *   - a positive moment happened in the last 10 minutes AND it belongs to this
 *     role (a fee paid by a parent, attendance saved by a teacher, …);
 *   - first seen >= 7 days ago, >= 5 launches, >= 3 distinct active days;
 *   - no error toast / failed API call in the last 2 minutes;
 *   - not asked in the last 90 days;
 *   - fewer than 3 asks in the last 365 days (Apple's cap).
 * A dismissal, a "Not really" and a tap on Settings → Rate all count as an ask:
 * the point of the limits is to leave people alone, not to count sheets.
 *
 * Settings → My Profile → "Rate CampusHub AI" is the one path OUTSIDE this
 * policy: the person asked, so install age, launches, active days, the error
 * quiet period, the cooldown and the yearly cap do not apply to it (the OS
 * still applies its own quota). It is shown only to `REVIEW_ELIGIBLE_ROLES`
 * (`canRateFromSettings`), and a tap is still written down as an ask so the
 * automatic prompt stays away afterwards.
 *
 * Clocks: a stored time later than "now" (the device clock was set forward and
 * corrected) is clamped to now when read, so it can never freeze the feature
 * until that future date.
 *
 * "Install age" is "age of this record": the first launch of a build that has
 * this code. Existing users therefore start their 7 days on the update, which
 * errs on the quiet side. The record lives in localStorage; if the OS evicts it
 * the clock simply restarts (and Apple's own cap still applies).
 *
 * Wiring: launches are counted in `components/providers/native-lifecycle.tsx`
 * (no request), positive moments are recorded by the hook / modal that owns the
 * moment, errors by `api/client.ts` and `lib/toast-config.ts`, and
 * `components/app-review/app-review-host.tsx` asks the policy and shows the card.
 */
import { z } from "zod";
import { getPlatform, isNative } from "./platform";
import { istYmd } from "./ist-time";

// ── The one switch ──────────────────────────────────────────────────────────

/**
 * `true`  — a small "Enjoying CampusHub AI?" card comes first: "Yes, rate it"
 *           opens the store sheet, "Not really" opens our feedback form.
 * `false` — the card is skipped and the store sheet is requested directly when
 *           the policy says so. Nothing else changes: flip this constant and
 *           ship. (The card is `components/app-review/app-review-host.tsx` +
 *           `app-review-card.tsx`; with the flag off they never render.)
 */
export const APP_REVIEW_PRE_PROMPT = true;

// ── Policy numbers ──────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

export const APP_REVIEW_POLICY = {
  minInstallAgeDays: 7,
  minLaunches: 5,
  minActiveDays: 3,
  /** No error toast / failed API call this recently. */
  errorQuietMs: 2 * 60 * 1000,
  cooldownDays: 90,
  /** Apple's cap: 3 sheets per app per 365 days. */
  maxPromptsPerYear: 3,
  yearDays: 365,
  /** How long a positive moment stays "just happened". */
  momentTtlMs: 10 * 60 * 1000,
  /** Distinct active days remembered (we only ever need `minActiveDays`). */
  activeDaysKept: 60,
  /** Prompt history remembered (the yearly cap is 3). */
  promptsKept: 10,
} as const;

// ── Who, and which moments ──────────────────────────────────────────────────

/**
 * Roles that may be asked: the roles that can open /support (the feedback
 * route), which is exactly "every school role except DRIVER and KIOSK". The
 * platform tier (CONTROL_CENTER) is left out too — staff, not customers.
 */
export const REVIEW_ELIGIBLE_ROLES: readonly string[] = [
  "CHAIRMAN",
  "ADMIN",
  "DIRECTOR",
  "PRINCIPAL",
  "TEACHER",
  "STAFF",
  "PARENT",
  "STUDENT",
];

/** Settings -> Rate CampusHub AI is shown to exactly the roles the automatic prompt may ask. */
export function canRateFromSettings(role: string | null | undefined): boolean {
  return Boolean(role) && REVIEW_ELIGIBLE_ROLES.includes(role as string);
}

export type ReviewMomentKind = "fee_paid" | "attendance_marked" | "homework_submitted" | "meeting_ended";

/** A moment only counts for the roles it is a win for. */
const MOMENT_ROLES: Record<ReviewMomentKind, readonly string[]> = {
  fee_paid: ["PARENT"],
  attendance_marked: ["TEACHER"],
  homework_submitted: ["STUDENT", "PARENT"],
  meeting_ended: REVIEW_ELIGIBLE_ROLES,
};

// ── Persisted state (Zod) ───────────────────────────────────────────────────

export const REVIEW_STATE_KEY = "ch-app-review-v1";

export const promptOutcomeSchema = z.enum([
  /** "Yes, rate it", or the direct request when the card is switched off. */
  "sheet_requested",
  /** "Not really" — sent to the feedback form. */
  "not_really",
  /** Closed the card without answering (X, scrim, Android back). */
  "dismissed",
  /** Settings → Rate CampusHub AI. */
  "settings",
]);
export type PromptOutcome = z.infer<typeof promptOutcomeSchema>;

const promptEntrySchema = z.object({ at: z.number().int().nonnegative(), outcome: promptOutcomeSchema });
const promptsSchema = z.array(promptEntrySchema).max(APP_REVIEW_POLICY.promptsKept);

export const reviewStateSchema = z.object({
  v: z.literal(1),
  /** Epoch ms of the first launch that recorded anything. */
  firstSeenAt: z.number().int().nonnegative(),
  launches: z.number().int().nonnegative(),
  /** Distinct local calendar days (YYYY-MM-DD) the app was opened, oldest first. */
  activeDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(APP_REVIEW_POLICY.activeDaysKept),
  prompts: promptsSchema,
});
export type ReviewState = z.infer<typeof reviewStateSchema>;

export function emptyReviewState(now: number): ReviewState {
  return { v: 1, firstSeenAt: now, launches: 0, activeDays: [], prompts: [] };
}

/** Times from the future (a clock that was set forward and corrected) are treated as "now". */
function clampToNow(state: ReviewState, now: number): ReviewState {
  const futureStamp = state.firstSeenAt > now || state.prompts.some((p) => p.at > now);
  if (!futureStamp) return state;
  return {
    ...state,
    firstSeenAt: Math.min(state.firstSeenAt, now),
    prompts: state.prompts.map((p) => (p.at > now ? { ...p, at: now } : p)),
  };
}

/**
 * Anything unreadable (missing, corrupt, wrong shape, future version) is a fresh
 * record, never a throw. A fresh record restarts install age, but if the stored
 * JSON still carries a valid list of past asks (say only the version differs),
 * those are kept so the 90-day cooldown and the yearly cap survive.
 */
export function parseReviewState(raw: string | null | undefined, now: number): ReviewState {
  return clampToNow(readRecord(raw, now), now);
}

/** `parseReviewState` before the clock repair, so the loader can tell whether it changed anything. */
function readRecord(raw: string | null | undefined, now: number): ReviewState {
  if (!raw) return emptyReviewState(now);
  try {
    const json: unknown = JSON.parse(raw);
    const parsed = reviewStateSchema.safeParse(json);
    if (parsed.success) return parsed.data;
    const prompts = promptsSchema.safeParse((json as { prompts?: unknown } | null)?.prompts);
    return { ...emptyReviewState(now), prompts: prompts.success ? prompts.data : [] };
  } catch {
    return emptyReviewState(now);
  }
}

export function serializeReviewState(state: ReviewState): string {
  return JSON.stringify(state);
}

// ── Pure state transitions ──────────────────────────────────────────────────

/** Today joins the active days (once). */
export function withActiveDay(state: ReviewState, now: number): ReviewState {
  const today = istYmd(new Date(now));
  if (state.activeDays.includes(today)) return state;
  return { ...state, activeDays: [...state.activeDays, today].slice(-APP_REVIEW_POLICY.activeDaysKept) };
}

/** One cold start: +1 launch, and today is an active day. */
export function withLaunch(state: ReviewState, now: number): ReviewState {
  return { ...withActiveDay(state, now), launches: state.launches + 1 };
}

/** Write an ask down. Entries older than the yearly window can never matter again and are dropped. */
export function withPrompt(state: ReviewState, outcome: PromptOutcome, now: number): ReviewState {
  const horizon = now - APP_REVIEW_POLICY.yearDays * DAY_MS;
  const recent = clampToNow(state, now).prompts.filter((p) => p.at > horizon);
  return { ...state, prompts: [...recent, { at: now, outcome }].slice(-APP_REVIEW_POLICY.promptsKept) };
}

// ── The policy ──────────────────────────────────────────────────────────────

export type ReviewBlockReason =
  | "not_native"
  | "role"
  | "no_moment"
  | "moment_role"
  | "moment_stale"
  | "install_age"
  | "launches"
  | "active_days"
  | "recent_error"
  | "cooldown"
  | "yearly_cap";

export interface ReviewMoment {
  kind: ReviewMomentKind;
  at: number;
}

export interface EligibilityInput {
  state: ReviewState;
  now: number;
  native: boolean;
  role: string | null | undefined;
  moment: ReviewMoment | null;
  /** Epoch ms of the last error toast / failed API call this session, if any. */
  lastErrorAt: number | null;
}

export type Eligibility = { eligible: true } | { eligible: false; reason: ReviewBlockReason };

/** First failing rule wins; the order is the order a person would explain it in. */
export function evaluateEligibility(input: EligibilityInput): Eligibility {
  const { state, now, native, role, moment, lastErrorAt } = input;
  const P = APP_REVIEW_POLICY;
  const no = (reason: ReviewBlockReason): Eligibility => ({ eligible: false, reason });

  if (!native) return no("not_native");
  if (!role || !REVIEW_ELIGIBLE_ROLES.includes(role)) return no("role");
  if (!moment) return no("no_moment");
  if (!MOMENT_ROLES[moment.kind].includes(role)) return no("moment_role");
  if (now - moment.at > P.momentTtlMs) return no("moment_stale");
  if (now - state.firstSeenAt < P.minInstallAgeDays * DAY_MS) return no("install_age");
  if (state.launches < P.minLaunches) return no("launches");
  if (new Set(state.activeDays).size < P.minActiveDays) return no("active_days");
  if (lastErrorAt !== null && now - lastErrorAt < P.errorQuietMs) return no("recent_error");

  // A stored time in the future counts as "now", never as a reason to wait until then.
  const asks = clampToNow(state, now).prompts;
  const last = asks.reduce((m, p) => Math.max(m, p.at), 0);
  if (last > 0 && now - last < P.cooldownDays * DAY_MS) return no("cooldown");
  const inYear = asks.filter((p) => now - p.at < P.yearDays * DAY_MS).length;
  if (inYear >= P.maxPromptsPerYear) return no("yearly_cap");

  return { eligible: true };
}

// ── Storage (native-safe: every access can throw or come back empty) ────────

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function loadReviewState(now: number = Date.now(), storage: Storage | null = safeStorage()): ReviewState {
  try {
    const record = readRecord(storage?.getItem(REVIEW_STATE_KEY), now);
    const state = clampToNow(record, now);
    // Write the clock repair back: left in memory only, a stamp from the future
    // would be re-clamped to every later "now" and never age.
    if (state !== record) saveReviewState(state, storage);
    return state;
  } catch {
    return emptyReviewState(now);
  }
}

export function saveReviewState(state: ReviewState, storage: Storage | null = safeStorage()): void {
  try {
    storage?.setItem(REVIEW_STATE_KEY, serializeReviewState(state));
  } catch {
    /* private mode / quota: the policy just stays conservative */
  }
}

let launchRecorded = false;

/**
 * Count this cold start. Native only, synchronous, no network. The guard keeps a
 * remount of the lifecycle provider (StrictMode, HMR) from counting twice.
 */
export function recordAppLaunch(now: number = Date.now()): void {
  if (!isNative() || launchRecorded) return;
  launchRecorded = true;
  const state = loadReviewState(now);
  saveReviewState(withLaunch(state, now));
}

/**
 * The app came back to the foreground. A phone that is never cold-started still
 * has days of use, so the day counts, the launch does not.
 */
export function recordAppResume(now: number = Date.now()): void {
  if (!isNative()) return;
  const state = loadReviewState(now);
  const next = withActiveDay(state, now);
  if (next !== state) saveReviewState(next);
}

/** Write an ask down. Call BEFORE making it: the stores never say whether it was shown. */
export function recordPrompt(outcome: PromptOutcome, now: number = Date.now()): void {
  saveReviewState(withPrompt(loadReviewState(now), outcome, now));
}

// ── Session signals (memory only: nothing from last session is "just now") ──

let lastErrorAt: number | null = null;
let pendingMoment: ReviewMoment | null = null;
const momentListeners = new Set<() => void>();

/** An error toast was shown or an API call failed. */
export function noteAppError(now: number = Date.now()): void {
  lastErrorAt = now;
}

/**
 * Failed API calls that count as "something went wrong for this person". A 401
 * (silent token refresh), 403 (role gate swallowed by the client) and 404 (a
 * background read racing a delete) are routine and say nothing about the app.
 */
export function noteApiFailure(status: number | undefined, now: number = Date.now()): void {
  if (status === 401 || status === 403 || status === 404) return;
  noteAppError(now);
}

export function getLastAppErrorAt(): number | null {
  return lastErrorAt;
}

/**
 * Was this the end of a call worth a good feeling? Hung up or ended by the host,
 * after at least two minutes — not a drop, a removal or a misdial.
 */
export function isPositiveCallEnd(reason: string, seconds: number): boolean {
  return (reason === "left" || reason === "ended" || reason === "you-ended") && seconds >= 120;
}

/** Something just went well. No-op in the browser. The host decides whether it earns an ask. */
export function recordPositiveMoment(kind: ReviewMomentKind, now: number = Date.now()): void {
  if (!isNative()) return;
  pendingMoment = { kind, at: now };
  momentListeners.forEach((fn) => fn());
}

export function getPendingMoment(): ReviewMoment | null {
  return pendingMoment;
}

export function clearPendingMoment(): void {
  pendingMoment = null;
}

export function subscribeToMoments(listener: () => void): () => void {
  momentListeners.add(listener);
  return () => {
    momentListeners.delete(listener);
  };
}

/** Test seam: forget every session signal and the launch guard. */
export function resetAppReviewForTests(): void {
  lastErrorAt = null;
  pendingMoment = null;
  launchRecorded = false;
  momentListeners.clear();
}

/** The policy, fed from live state. The host calls this at the moment it would show something. */
export function checkEligibilityNow(role: string | null | undefined, now: number = Date.now()): Eligibility {
  return evaluateEligibility({
    state: loadReviewState(now),
    now,
    native: isNative(),
    role,
    moment: pendingMoment,
    lastErrorAt,
  });
}

// ── The native call ─────────────────────────────────────────────────────────

/**
 * Ask the OS to show its rating sheet.
 *
 * Browser: does nothing. Native: hands the request to `InAppReview`
 * (`@capacitor-community/in-app-review` — SKStoreReviewController on iOS, Play
 * Core ReviewManager on Android) and resolves when the OS is done with it.
 * It also resolves when the OS declined, when Play is unavailable (a sideloaded
 * APK, no Play account) and when the plugin is missing from an old shell:
 * nothing about a rating request is worth an error toast. Never rejects.
 *
 * The plugin handle is used inside this function and never returned or resolved
 * with: a Capacitor plugin proxy answers `then`, which hangs a promise (see the
 * note in secure-storage.ts).
 */
export async function requestStoreReview(): Promise<void> {
  if (!isNative()) return;
  try {
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
  } catch (err) {
    console.warn("[app-review] the OS did not take the rating request", err);
  }
}

// ── The store listing ("Write a review") ────────────────────────────────────

/**
 * TODO(owner): the App Store numeric id of the CampusHub AI listing, e.g.
 * "6740123456" (App Store Connect -> App Information -> Apple ID; or the digits
 * after `id` in the listing URL). Until it is set the iOS "Write a review" link
 * is hidden: a review URL without the id would land on a search page.
 * Not a secret — it is in every public listing URL.
 */
export const IOS_APP_STORE_ID = "";

/** The Play package of the production app; staging/dev builds report their own via `App.getInfo().id`. */
export const ANDROID_PACKAGE_FALLBACK = "ai.campushub.app";

/** The URL that opens the store's "write a review" screen, or null when it cannot be built. */
export function storeReviewUrl(
  platform: "ios" | "android" | "web",
  opts: { iosAppId?: string; androidPackage?: string } = {},
): string | null {
  if (platform === "ios") {
    const id = (opts.iosAppId ?? IOS_APP_STORE_ID).trim();
    return /^\d{6,}$/.test(id) ? `itms-apps://itunes.apple.com/app/id${id}?action=write-review` : null;
  }
  if (platform === "android") {
    const pkg = (opts.androidPackage ?? ANDROID_PACKAGE_FALLBACK).trim();
    return /^[A-Za-z][\w]*(\.[A-Za-z][\w]*)+$/.test(pkg) ? `market://details?id=${pkg}` : null;
  }
  return null;
}

/** Can this device offer a "Write a review" link at all? */
export function canOpenStoreReviewPage(): boolean {
  return isNative() && storeReviewUrl(getPlatform()) !== null;
}

/** Open the store's review screen. Returns false when there is nowhere to go. */
export async function openStoreReviewPage(): Promise<boolean> {
  if (!isNative()) return false;
  const platform = getPlatform();
  let androidPackage: string | undefined;
  if (platform === "android") {
    try {
      const { App } = await import("@capacitor/app");
      androidPackage = (await App.getInfo()).id;
    } catch {
      androidPackage = undefined;
    }
  }
  const url = storeReviewUrl(platform, { androidPackage });
  if (!url) return false;
  window.open(url, "_system");
  return true;
}

/** Where "Not really" goes: the Support form, opened on the app-feedback shape. `source` is how we tell where it came from. */
export const APP_REVIEW_SOURCE = "app-review";
export const APP_REVIEW_FEEDBACK_PATH = `/support/new?source=${APP_REVIEW_SOURCE}`;
